import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  makeStyles,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { PhotoCard, PhotoCardHandle } from "./photoCard";
import { AddPhotosCard } from "./addPhotosCard";
import { useStore } from "@roc/client-portal-shared/hooks";
import { observer } from "mobx-react";
import { InspectionPhoto } from "../types/types";

const useStyles = makeStyles(() => ({
  accordion: {
    margin: 0,
    padding: 0,
    boxShadow: "none",
    border: "none",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: "8px 0",
    },
  },
  accordionSummary: {
    background: '#eee',
    borderBottom: "1px solid #aaa",
    minHeight: "auto",
    '&.Mui-expanded': {
      borderBottom: "none",
      minHeight: "auto",
    },
    "& .MuiAccordionSummary-content": {
      margin: "8px 0",
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: "8px 0",
    }
  }
}));

interface PhotosAccordionProps {
  inspectionTaskId: number;
  title: string;
  isManualUploadMode?: boolean;
  /**
   * Whether the viewer can add photos to this section. Defaults to isManualUploadMode so any
   * existing caller that doesn't pass it keeps today's behavior exactly. Lets inspectionReport.tsx
   * show the Manual display style (isManualUploadMode) in a read-only context (e.g. the
   * Inspections Dashboard) without also enabling uploads there.
   */
  canUploadPhotos?: boolean;
  inspectionId?: number;
}

export interface PhotosAccordionHandle {
  triggerAnimation: (inspectionPhotoId: number) => void;
}

export const PhotosAccordion = observer(forwardRef<PhotosAccordionHandle, PhotosAccordionProps>(({ inspectionTaskId, title, isManualUploadMode, canUploadPhotos, inspectionId }, ref) => {
  const classes = useStyles();
  const { inspectionStore } = useStore();
  const { inspectionPhotosStore } = inspectionStore;

  const effectiveCanUpload = canUploadPhotos ?? isManualUploadMode;

  const photos =
    inspectionPhotosStore.inspectionTaskPhotos?.[inspectionTaskId] || [];

  return (
    <Accordion defaultExpanded className={classes.accordion}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${title}-content`}
        className={classes.accordionSummary}
      >
        <Typography variant="body2"><strong>{title}</strong></Typography>
      </AccordionSummary>
      <AccordionDetails>
        <PhotoList
          ref={ref}
          photos={photos}
          isManualUploadMode={isManualUploadMode}
          addPhotosCard={
            effectiveCanUpload && (
              <AddPhotosCard inspectionId={inspectionId} inspectionTaskId={inspectionTaskId} />
            )
          }
        />
      </AccordionDetails>
    </Accordion>
  );
}));

interface PhotoListProps {
  photos: InspectionPhoto[];
  isManualUploadMode?: boolean;
  addPhotosCard?: React.ReactNode;
}

export const PhotoList = observer(
  forwardRef<PhotosAccordionHandle, PhotoListProps>(
    ({ photos, isManualUploadMode, addPhotosCard }, ref) => {
      const classes = useStyles();
      const { inspectionStore } = useStore();
      const { inspectionPhotosStore } = inspectionStore;

      // Refs
      const containerRef = useRef<HTMLDivElement>(null); // for scrollIntoView
      const cardsRefs = useRef<{ [key: number]: PhotoCardHandle }>({}); // to triggerAnimation

      const hasPhotos = photos?.length > 0;

      const onPhotoClickedHandler = (inspectionPhoto: InspectionPhoto) => {
        inspectionStore.setInspectionPhotoSelected(inspectionPhoto);
      };

      useImperativeHandle(ref, () => ({
        triggerAnimation: (inspectionPhotoId: number) => {
          containerRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
          setTimeout(() => {
            cardsRefs.current[inspectionPhotoId]?.triggerAnimation();
          }, 500);
        },
      }));

      return (
        <div ref={containerRef}>
          {hasPhotos || addPhotosCard ? (
            <Box display="flex" gridGap="1rem" flexWrap="wrap">
              {addPhotosCard}
              {photos.map(photo => {
                const url = photo.thumbnailImage || photo.image;
                const serverDate = photo.captureTimeIso
                  ? new Date(photo.captureTimeIso)
                  : null;
                const hasIssues = inspectionPhotosStore.photoHasIssues(photo);
                const hasManualValidation = inspectionPhotosStore.photoHasManualValidation(
                  photo
                );
                const preferredRotation = photo.preferredRotation;
                const caption = photo.caption;

                return (
                  <PhotoCard
                    ref={el =>
                      (cardsRefs.current[photo.inspectionPhotoId] = el)
                    }
                    key={photo.inspectionPhotoId}
                    url={url}
                    preferredRotation={preferredRotation}
                    hasIssues={hasIssues}
                    hasManualValidation={hasManualValidation}
                    serverDate={serverDate}
                    onClick={() => onPhotoClickedHandler(photo)}
                    caption={caption}
                    isReported={photo.reported}
                    // Belt-and-suspenders on top of isManualUploadMode — a DESKTOP photo never has
                    // validation data, regardless of what the caller passed.
                    showStatusIcon={!isManualUploadMode && photo.source !== 'DESKTOP'}
                  />
                );
              })}
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="start"
              alignItems="center"
              width="100%"
              pt={1}
            >
              <Typography variant="body2" style={{ opacity: 0.4 }}>
                No Photos Available.
              </Typography>
            </Box>
          )}
        </div>
      );
    }
  )
);